<template>
  <div ref="container" class="notification-bell">
    <button
      class="btn btn-icon btn-link btn-menu-action notification-bell__trigger"
      @click="togglePanel"
    >
      <fa-icon :icon="['fal', 'bell']" />
      <span v-if="hasUnread" class="notification-bell__indicator">{{ badgeLabel }}</span>
    </button>
    <notification-panel
      v-if="isPanelVisible"
      :items="notifications"
      :is-loading="isLoading"
      :show-mark-all="unreadCount > 0"
      @mark-all="handleMarkAll"
      @open="openScreen"
    />
    <notification-screen
      v-if="screen"
      :show="!!screen"
      :notification="screen"
      @close="closeScreen"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import AblyService from '@/services/ably';
import NotificationPanel from './NotificationPanel.vue';
import NotificationScreen from './NotificationScreen.vue';

const OUTSIDE_EVENTS = ['click', 'touchstart'];

export default {
  components: {
    NotificationPanel,
    NotificationScreen,
  },
  data() {
    return {
      isPanelVisible: false,
      hasLoadedList: false,
      channel: null,
      screen: null,
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      notificationsState: state => state.notifications,
    }),
    ...mapGetters({
      isSetBranch: 'isSetBranch',
      isProfessional: 'isProfessional',
      isCollaborator: 'isCollaborator',
    }),
    notifications() {
      return this.notificationsState.items;
    },
    unreadCount() {
      return this.notificationsState.unreadCount;
    },
    isLoading() {
      return this.notificationsState.isLoading;
    },
    hasUnread() {
      return this.unreadCount > 0;
    },
    badgeLabel() {
      if (this.unreadCount > 9) {
        return '9+';
      }
      return String(this.unreadCount);
    },
  },
  mounted() {
    this.bootstrap();
  },
  beforeDestroy() {
    this.detachOutsideEvents();
    if (this.channel) {
      this.channel.unsubscribe();
    }
  },
  methods: {
    async bootstrap() {
      if (!this.isSetBranch) {
        return;
      }
      try {
        await this.$store.dispatch('refreshUnreadCount');
      } catch {
        await this.$store.dispatch('loadNotifications').catch(() => {});
      }
      this.initRealtime();
      this.loadPendingScreen();
    },
    loadPendingScreen() {
      return this.$httpX.get('/notifications/pending-modal')
        .then(({ data }) => {
          this.screen = data.notification;
        })
        .catch(() => {});
    },
    openScreen(notification) {
      this.closePanel();
      this.screen = notification;
    },
    closeScreen() {
      const { id, read } = this.screen;
      this.screen = null;
      if (!read) {
        this.$store.dispatch('markAsRead', id).catch(() => {});
      }
    },
    isAudience({ audience }) {
      if (audience === 'professional') {
        return this.isProfessional;
      }
      if (audience === 'collaborator') {
        return this.isCollaborator && !this.isProfessional;
      }
      return true;
    },
    initRealtime() {
      const service = AblyService.instance(this.user.id);
      if (!service || !service.realtime) {
        return;
      }
      this.channel = service.realtime.channels.get('global:notifications');
      this.channel.subscribe((event) => {
        if (event.name === 'created' && this.isAudience(event.data)) {
          this.$store.dispatch('prependNotification', event.data);
        }
      });
    },
    async togglePanel() {
      if (this.isPanelVisible) {
        this.closePanel();
      } else {
        await this.openPanel();
      }
    },
    async openPanel() {
      this.isPanelVisible = true;
      this.attachOutsideEvents();
      try {
        await this.$store.dispatch('loadNotifications');
        this.hasLoadedList = true;
      } catch {
        this.hasLoadedList = false;
      }
    },
    closePanel() {
      this.isPanelVisible = false;
      this.detachOutsideEvents();
    },
    attachOutsideEvents() {
      OUTSIDE_EVENTS.forEach((event) => {
        document.addEventListener(event, this.handleOutsideEvent, true);
      });
    },
    detachOutsideEvents() {
      OUTSIDE_EVENTS.forEach((event) => {
        document.removeEventListener(event, this.handleOutsideEvent, true);
      });
    },
    handleOutsideEvent(event) {
      if (!this.isPanelVisible) {
        return;
      }
      if (!this.$refs.container) {
        this.closePanel();
        return;
      }
      const { target } = event;
      if (!this.$refs.container.contains(target)) {
        this.closePanel();
      }
    },
    async handleMarkAll() {
      await this.$store.dispatch('markAllAsRead').catch(() => null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.notification-bell {
  position: relative;

  &__trigger {
    position: relative;
  }

  &__indicator {
    align-items: center;
    background-color: $red-color-500;
    border-radius: 999px;
    color: $light-color;
    display: flex;
    font-size: 0.55rem;
    font-weight: 600;
    height: 1rem;
    justify-content: center;
    min-width: 1rem;
    padding: 0 0.3rem;
    position: absolute;
    right: -0.2rem;
    top: -0.2rem;
  }
}
</style>
